import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Users, Plus, Trash2, ChevronDown, ChevronUp, ArrowUp, ArrowDown,
  CheckCircle, AlertCircle, ArrowLeft, UserPlus, LogOut, Save, Edit3, XCircle, MessageCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import DISTRICTS_BY_STATE from '../../data/indiaDistricts';
import { FamilyFormSection } from '../../components/FamilySections';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FamilyEditPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState({
    states: [],
    nukhs: [],
    mainBranches: [],
    education: [],
    occupation: [],
    maritalStatus: [],
    relations: []
  });
  
  const [familyData, setFamilyData] = useState({
    currentPlace: '',
    currentCity: '',
    currentDistrict: '',
    district: '',
    state: '',
    country: 'India',
    originPlace: '',
    originCity: '',
    originDistrict: '',
    originState: '',
    originCountry: 'India',
    familyNukh: ''
  });
  
  const [familyMembers, setFamilyMembers] = useState([]);
  const [expandedMembers, setExpandedMembers] = useState([0]);
  const [headName, setHeadName] = useState('');
  const [familyStatus, setFamilyStatus] = useState('');
  const [familyRejectionReason, setFamilyRejectionReason] = useState('');
  const [familyAdminComments, setFamilyAdminComments] = useState('');
  const [sameAsCurrent, setSameAsCurrent] = useState(false);
  const handleSameAsCurrent = (checked) => {
    setSameAsCurrent(checked);
    if (checked) {
      setFamilyData(prev => ({
        ...prev,
        originCountry: prev.country,
        originState: prev.state,
        originDistrict: prev.currentDistrict,
        originCity: prev.currentCity,
        originPlace: prev.currentPlace,
      }));
    }
  };

  // Check auth and fetch data
  useEffect(() => {
    const token = localStorage.getItem('familyToken');
    
    if (!token) {
      toast.error('Please login first');
      navigate('/auth?tab=family');
      return;
    }
    
    setHeadName(localStorage.getItem('familyHeadName') || 'Family Head');
    fetchOptions();
    fetchFamilyData();
  }, []);

  const fetchOptions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/family-registration/options`);
      if (response.ok) {
        const data = await response.json();
        setOptions(data);
      }
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const fetchFamilyData = async () => {
    try {
      const token = localStorage.getItem('familyToken');
      
      const response = await fetch(`${API_URL}/api/family/my-family`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFamilyData({
          currentPlace: data.currentPlace || '',
          currentCity: data.currentCity || '',
          currentDistrict: data.currentDistrict || data.district || '',
          district: data.district || '',
          state: data.state || '',
          country: data.country || 'India',
          originPlace: data.originPlace || '',
          originCity: data.originCity || '',
          originDistrict: data.originDistrict || '',
          originState: data.originState || '',
          originCountry: data.originCountry || 'India',
          familyNukh: data.familyNukh || ''
        });
        setFamilyMembers(data.familyMembers || []);
        setFamilyStatus(data.status || '');
        setFamilyRejectionReason(data.rejectionReason || '');
        setFamilyAdminComments(data.adminComments || '');
        setLoading(false);
      } else if (response.status === 401) {
        localStorage.removeItem('familyToken');
        toast.error('Session expired. Please login again.');
        navigate('/auth?tab=family');
      } else {
        toast.error('Failed to load family data');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching family:', error);
      toast.error('Failed to load family data');
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('familyToken');
    localStorage.removeItem('familyId');
    localStorage.removeItem('familyHeadName');
    localStorage.removeItem('familyEmail');
    toast.success('Logged out successfully');
    navigate('/auth?tab=family');
  };

  // Create empty member object
  function createEmptyMember(isHead = false) {
    return {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      name: '',
      gender: 'Male',
      fatherName: '',
      nukh: '',
      spouseName: '',
      relationWithHead: isHead ? 'Head - Self' : '',
      dob: '',
      age: '',
      education: '',
      occupation: '',
      maritalStatus: '',
      bloodGroup: '',
      mobileNo: '',
      emailId: '',
      socialMediaId: '',
      maternalName: '',
      maternalNukh: '',
      maternalPlace: '',
      other: ''
    };
  }

  const calculateAge = (dob) => {
    if (!dob) return '';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age > 0 ? age : '';
  };

  const handleFamilyDataChange = (e) => {
    const { name, value } = e.target;
    setFamilyData(prev => ({ ...prev, [name]: value }));
  };

  const handleMemberChange = (index, field, value) => {
    const updatedMembers = [...familyMembers];
    updatedMembers[index] = { ...updatedMembers[index], [field]: value };
    
    if (field === 'dob') {
      updatedMembers[index].age = calculateAge(value);
    }
    
    setFamilyMembers(updatedMembers);
  };

  const addMember = () => {
    setFamilyMembers([...familyMembers, createEmptyMember(false)]);
    setExpandedMembers([...expandedMembers, familyMembers.length]);
  };

  const removeMember = (index) => {
    if (index === 0) {
      toast.error('Cannot remove the family head');
      return;
    }
    const updatedMembers = familyMembers.filter((_, i) => i !== index);
    setFamilyMembers(updatedMembers);
    setExpandedMembers(expandedMembers.filter(i => i !== index).map(i => i > index ? i - 1 : i));
  };

  // Reorder members. Head (index 0) is locked in place. direction: -1 = up, +1 = down.
  const moveMember = (index, direction) => {
    const target = index + direction;
    if (index <= 0 || target <= 0 || target >= familyMembers.length) return;
    const updated = [...familyMembers];
    [updated[index], updated[target]] = [updated[target], updated[index]];
    setFamilyMembers(updated);
    // Keep the "expanded" state moving with the cards so the user's UI doesn't jump.
    setExpandedMembers(prev => prev.map(i => {
      if (i === index) return target;
      if (i === target) return index;
      return i;
    }));
  };

  const toggleMemberExpand = (index) => {
    if (expandedMembers.includes(index)) {
      setExpandedMembers(expandedMembers.filter(i => i !== index));
    } else {
      setExpandedMembers([...expandedMembers, index]);
    }
  };

  const handleSave = async () => {
    // Validate family location — mirrors the create-form rules.
    if (!familyData.country?.trim()) {
      toast.error('Please select current country');
      return;
    }
    if (!familyData.state) {
      toast.error('Please select current state');
      return;
    }
    if (!(familyData.currentDistrict || familyData.district)) {
      toast.error('Please select current district');
      return;
    }
    if (!familyData.currentCity?.trim()) {
      toast.error('Please select or enter current city / village');
      return;
    }
    if (!familyData.currentPlace?.trim()) {
      toast.error('Please enter current address');
      return;
    }
    if (!familyData.familyNukh) {
      toast.error('Please select family nukh');
      return;
    }

    // Validate each member — mirrors the create-form rules.
    // Mobile number is required only for member 0 (Family Head).
    for (let i = 0; i < familyMembers.length; i++) {
      const member = familyMembers[i];
      const memberLabel = member.name || `Member ${i + 1}`;
      const expandIfNeeded = () => setExpandedMembers(prev => prev.includes(i) ? prev : [...prev, i]);

      if (!member.name?.trim()) {
        toast.error(`Please enter name for member ${i + 1}`);
        expandIfNeeded();
        return;
      }
      if (!member.gender) {
        toast.error(`Please select gender for ${memberLabel}`);
        expandIfNeeded();
        return;
      }
      if (!member.relationWithHead) {
        toast.error(`Please select relation with head for ${memberLabel}`);
        expandIfNeeded();
        return;
      }
      if (!member.fatherName?.trim()) {
        toast.error(`Please enter father's name for ${memberLabel}`);
        expandIfNeeded();
        return;
      }
      if (!member.nukh) {
        toast.error(`Please select nukh for ${memberLabel}`);
        expandIfNeeded();
        return;
      }
      if (!member.dob) {
        toast.error(`Please enter date of birth for ${memberLabel}`);
        expandIfNeeded();
        return;
      }
      if (!member.education) {
        toast.error(`Please select education for ${memberLabel}`);
        expandIfNeeded();
        return;
      }
      if (!member.occupation) {
        toast.error(`Please select occupation for ${memberLabel}`);
        expandIfNeeded();
        return;
      }
      if (!member.maritalStatus) {
        toast.error(`Please select marital status for ${memberLabel}`);
        expandIfNeeded();
        return;
      }
      if (i === 0 && !member.mobileNo?.trim()) {
        toast.error(`Please enter mobile number for ${memberLabel} (Family Head)`);
        expandIfNeeded();
        return;
      }
      if (!member.maternalName?.trim()) {
        toast.error(`Please enter maternal name (नाना/मामा का नाम) for ${memberLabel}`);
        expandIfNeeded();
        return;
      }
      if (!member.maternalNukh) {
        toast.error(`Please select maternal nukh for ${memberLabel}`);
        expandIfNeeded();
        return;
      }
      if (!member.maternalPlace?.trim()) {
        toast.error(`Please enter maternal place for ${memberLabel}`);
        expandIfNeeded();
        return;
      }
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('familyToken');
      const payload = {
        ...familyData,
        familyMembers: familyMembers.map(member => ({
          ...member,
          age: member.age ? parseInt(member.age) : null
        }))
      };
      
      const response = await fetch(`${API_URL}/api/family/my-family`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const data = await response.json();
        const newStatus = data.family?.status || data.status;
        if (newStatus === 'pending') {
          setFamilyStatus('pending');
          toast.success('Family data updated! Changes will reflect after admin approval.');
        } else {
          toast.success('Family data updated successfully!');
        }
      } else if (response.status === 401) {
        localStorage.removeItem('familyToken');
        toast.error('Session expired. Please login again.');
        navigate('/auth?tab=family');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save changes');
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your family data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="family-edit-page">
      {/* Header */}
      <div className="gradient-orange py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-white/80 hover:text-white">
                <ArrowLeft className="w-6 h-6" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Edit3 className="w-6 h-6" />
                  Edit Family Data
                </h1>
                <p className="text-white/80 text-sm">Welcome, {headName}</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status Banners */}
        {familyStatus === 'pending' && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-3" data-testid="family-status-pending">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-yellow-800">Registration Under Review</p>
              <p className="text-sm text-yellow-700">Your changes are pending admin approval. They will reflect on the portal after approval.</p>
            </div>
          </div>
        )}
        {familyStatus === 'rejected' && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="family-status-rejected">
            <div className="flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">Registration Rejected / रजिस्ट्रेशन अस्वीकार</p>
                {familyRejectionReason && (
                  <p className="text-sm text-red-700 mt-1"><span className="font-semibold">Reason:</span> {familyRejectionReason}</p>
                )}
                <p className="text-sm text-red-600 mt-1">Please edit your details and save to resubmit for approval.</p>
              </div>
            </div>
          </div>
        )}
        {familyAdminComments && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="family-user-admin-comments">
            <div className="flex items-start gap-3">
              <MessageCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Admin Feedback / प्रशासन की टिप्पणी</p>
                <p className="text-sm text-amber-700 mt-1 whitespace-pre-wrap">{familyAdminComments}</p>
                <p className="text-xs text-amber-500 mt-2">Please address the feedback above and re-submit.</p>
              </div>
            </div>
          </div>
        )}
        {/* Family Location Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-md p-6 mb-6"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2.5">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 text-primary shrink-0">
              <Users className="w-5 h-5" />
            </span>
            Family Location Details
            <span className="text-sm font-normal text-gray-500">(परिवार स्थान विवरण)</span>
          </h2>
          
          <div className="mb-4">
            <h3 className="font-semibold text-emerald-800 mb-3">Current Place / वर्तमान निवास</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country / देश <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="country"
                  value={familyData.country}
                  onChange={handleFamilyDataChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State / राज्य <span className="text-red-500">*</span>
                </label>
                <select
                  name="state"
                  value={familyData.state}
                  onChange={handleFamilyDataChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="edit-state"
                >
                  <option value="">Select State</option>
                  {options.states?.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  District / जिला <span className="text-red-500">*</span>
                </label>
                <select
                  name="currentDistrict"
                  value={familyData.currentDistrict}
                  onChange={handleFamilyDataChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="edit-current-district"
                  disabled={!familyData.state}
                >
                  <option value="">Select District</option>
                  {(DISTRICTS_BY_STATE[familyData.state] || []).map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City / Village / शहर / गाँव <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="currentCity"
                  value={familyData.currentCity}
                  onChange={handleFamilyDataChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="edit-current-city"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address / पता <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="currentPlace"
                  value={familyData.currentPlace}
                  onChange={handleFamilyDataChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="edit-current-place"
                />
              </div>
            </div>
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="font-semibold text-orange-800">Native Place / मूल निवास</h3>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none" data-testid="edit-same-as-current">
                <input
                  type="checkbox"
                  checked={sameAsCurrent}
                  onChange={(e) => handleSameAsCurrent(e.target.checked)}
                  className="w-4 h-4 accent-primary rounded"
                />
                Same as Current Place <span className="text-gray-400 text-xs">(वर्तमान निवास के समान)</span>
              </label>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country / देश
                </label>
                <input
                  type="text"
                  name="originCountry"
                  value={familyData.originCountry}
                  onChange={handleFamilyDataChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State / राज्य
                </label>
                <select
                  name="originState"
                  value={familyData.originState}
                  onChange={handleFamilyDataChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="edit-origin-state"
                >
                  <option value="">Select State</option>
                  {options.states?.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  District / जिला
                </label>
                <select
                  name="originDistrict"
                  value={familyData.originDistrict}
                  onChange={handleFamilyDataChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="edit-origin-district"
                  disabled={!familyData.originState}
                >
                  <option value="">Select District</option>
                  {(DISTRICTS_BY_STATE[familyData.originState] || []).map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City / Village / शहर / गाँव
                </label>
                <input
                  type="text"
                  name="originCity"
                  value={familyData.originCity}
                  onChange={handleFamilyDataChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="edit-origin-city"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address / पता
                </label>
                <input
                  type="text"
                  name="originPlace"
                  value={familyData.originPlace}
                  onChange={handleFamilyDataChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
          </div>
            
          <div className="grid md:grid-cols-2 gap-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Family Nukh <span className="text-red-500">*</span>
              </label>
              <select
                name="familyNukh"
                value={familyData.familyNukh}
                onChange={handleFamilyDataChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                data-testid="edit-family-nukh"
              >
                <option value="">Select Nukh</option>
                {options.nukhs?.map(nukh => (
                  <option key={nukh} value={nukh}>{nukh}</option>
                ))}
              </select>
            </div>
        </motion.div>

        {/* Family Members */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-md p-6 mb-6"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2.5">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 text-primary shrink-0">
              <UserPlus className="w-5 h-5" />
            </span>
            Family Members ({familyMembers.length})
            <span className="text-sm font-normal text-gray-500">(परिवार के सदस्य)</span>
          </h2>
          
          <div className="space-y-4">
            {familyMembers.map((member, index) => (
              <div 
                key={member.id || index} 
                className="border border-gray-200 rounded-lg overflow-hidden"
                data-testid={`edit-member-${index}`}
              >
                {/* Member Header */}
                <div 
                  className={`flex items-center justify-between p-4 cursor-pointer ${
                    index === 0 ? 'bg-primary/5' : 'bg-gray-50'
                  }`}
                  onClick={() => toggleMemberExpand(index)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                      index === 0 ? 'bg-primary' : 'bg-gray-400'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {member.name || `Member ${index + 1}`}
                        {index === 0 && (
                          <span className="ml-2 px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                            Head
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-gray-500">
                        {member.relationWithHead} • {member.gender}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {index !== 0 && (
                      <>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); moveMember(index, -1); }}
                          disabled={index <= 1}
                          title="Move up"
                          data-testid={`edit-member-move-up-${index}`}
                          className="p-2 text-gray-500 hover:text-primary hover:bg-primary/10 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ArrowUp className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); moveMember(index, 1); }}
                          disabled={index >= familyMembers.length - 1}
                          title="Move down"
                          data-testid={`edit-member-move-down-${index}`}
                          className="p-2 text-gray-500 hover:text-primary hover:bg-primary/10 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ArrowDown className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeMember(index);
                          }}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {expandedMembers.includes(index) ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
                
                {/* Member Form */}
                {expandedMembers.includes(index) && (
                  <div className="p-4 border-t border-gray-200 bg-white">
                      <FamilyFormSection sectionKey="basic" columns={3}>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={member.name || ''}
                            onChange={(e) => handleMemberChange(index, 'name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Relation with Head <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={member.relationWithHead || ''}
                            onChange={(e) => handleMemberChange(index, 'relationWithHead', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                            disabled={index === 0}
                          >
                            {index === 0 ? (
                              <option value="Head - Self">Head - Self</option>
                            ) : (
                              <>
                                <option value="">Select Relation</option>
                                {options.relations?.filter(r => r !== 'Head - Self').map(rel => (
                                  <option key={rel} value={rel}>{rel}</option>
                                ))}
                              </>
                            )}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Gender <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={member.gender || 'Male'}
                            onChange={(e) => handleMemberChange(index, 'gender', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          >
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                          </select>
                        </div>
                      </FamilyFormSection>
                      <FamilyFormSection sectionKey="personal" columns={3}>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Marital Status <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={member.maritalStatus || ''}
                            onChange={(e) => handleMemberChange(index, 'maritalStatus', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          >
                            <option value="">Select Status</option>
                            {options.maritalStatus?.map(status => (
                              <option key={status} value={status}>{status}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date of Birth <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={member.dob || ''}
                            onChange={(e) => handleMemberChange(index, 'dob', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Age (Years)
                          </label>
                          <input
                            type="number"
                            value={member.age || ''}
                            onChange={(e) => handleMemberChange(index, 'age', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm bg-gray-50"
                            placeholder="Auto-calculated"
                            readOnly={!!member.dob}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Education <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={member.education || ''}
                            onChange={(e) => handleMemberChange(index, 'education', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          >
                            <option value="">Select Education</option>
                            {options.education?.map(edu => (
                              <option key={edu} value={edu}>{edu}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Occupation <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={member.occupation || ''}
                            onChange={(e) => handleMemberChange(index, 'occupation', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          >
                            <option value="">Select Occupation</option>
                            {options.occupation?.map(occ => (
                              <option key={occ} value={occ}>{occ}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Blood Group <span className="text-gray-400 text-xs">(रक्त समूह — optional)</span>
                          </label>
                          <select
                            value={member.bloodGroup || ''}
                            onChange={(e) => handleMemberChange(index, 'bloodGroup', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                            data-testid={`edit-member-${index}-blood-group`}
                          >
                            <option value="">Select Blood Group</option>
                            {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bg => (
                              <option key={bg} value={bg}>{bg}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Spouse Name <span className="text-gray-400 text-xs">(जीवनसाथी — optional)</span>
                          </label>
                          <input
                            type="text"
                            value={member.spouseName || ''}
                            onChange={(e) => handleMemberChange(index, 'spouseName', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                            placeholder="Spouse name (if applicable)"
                            data-testid={`edit-member-${index}-spouse-name`}
                          />
                        </div>
                      </FamilyFormSection>
                      <FamilyFormSection sectionKey="paternal" columns={2}>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Father's Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={member.fatherName || ''}
                            onChange={(e) => handleMemberChange(index, 'fatherName', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Father's Nukh <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={member.nukh || ''}
                            onChange={(e) => handleMemberChange(index, 'nukh', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          >
                            <option value="">Select Nukh</option>
                            {options.nukhs?.map(nukh => (
                              <option key={nukh} value={nukh}>{nukh}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Mother's Name <span className="text-gray-400 text-xs">(माता का नाम — optional)</span>
                          </label>
                          <input
                            type="text"
                            value={member.motherName || ''}
                            onChange={(e) => handleMemberChange(index, 'motherName', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Mother's Nukh <span className="text-gray-400 text-xs">(माता का नुख — optional)</span>
                          </label>
                          <select
                            value={member.motherNukh || ''}
                            onChange={(e) => handleMemberChange(index, 'motherNukh', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          >
                            <option value="">Select Nukh</option>
                            {options.nukhs?.map(nukh => (
                              <option key={nukh} value={nukh}>{nukh}</option>
                            ))}
                          </select>
                        </div>
                      </FamilyFormSection>
                      <FamilyFormSection sectionKey="maternal" columns={3}>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Maternal Name (नाना/मामा का नाम) <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={member.maternalName || ''}
                            onChange={(e) => handleMemberChange(index, 'maternalName', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Maternal Nukh (मातृपक्ष नुख) <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={member.maternalNukh || ''}
                            onChange={(e) => handleMemberChange(index, 'maternalNukh', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          >
                            <option value="">Select Nukh</option>
                            {options.nukhs?.map(nukh => (
                              <option key={nukh} value={nukh}>{nukh}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Maternal Place (मातृपक्ष स्थान) <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={member.maternalPlace || ''}
                            onChange={(e) => handleMemberChange(index, 'maternalPlace', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          />
                        </div>
                      </FamilyFormSection>
                      <FamilyFormSection sectionKey="contact" columns={3}>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email
                          </label>
                          <input
                            type="email"
                            value={member.emailId || ''}
                            onChange={(e) => handleMemberChange(index, 'emailId', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Mobile No. {index === 0 && <span className="text-red-500">*</span>}
                          </label>
                          <input
                            type="tel"
                            value={member.mobileNo || ''}
                            onChange={(e) => handleMemberChange(index, 'mobileNo', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                            required={index === 0}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Social Media ID
                          </label>
                          <input
                            type="text"
                            value={member.socialMediaId || ''}
                            onChange={(e) => handleMemberChange(index, 'socialMediaId', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                            placeholder="@username"
                          />
                        </div>
                      </FamilyFormSection>
                      <FamilyFormSection sectionKey="other" columns={1}>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Other Information (अन्य जानकारी)
                          </label>
                          <textarea
                            value={member.other || ''}
                            onChange={(e) => handleMemberChange(index, 'other', e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                            placeholder="Any additional information about the member..."
                          />
                        </div>
                      </FamilyFormSection>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Add More Members Button */}
          <div className="mt-4 text-center">
            <Button
              type="button"
              variant="outline"
              onClick={addMember}
              className="border-dashed border-2"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Another Family Member
            </Button>
          </div>
        </motion.div>

        {/* Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6"
        >
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-blue-800">Family Summary</span>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-blue-700">{familyMembers.length}</p>
              <p className="text-sm text-blue-600">Total Members</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">
                {familyMembers.filter(m => m.gender === 'Male').length}
              </p>
              <p className="text-sm text-blue-600">Male</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">
                {familyMembers.filter(m => m.gender === 'Female').length}
              </p>
              <p className="text-sm text-blue-600">Female</p>
            </div>
          </div>
        </motion.div>

        {/* Save Button */}
        <div className="flex gap-4">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="flex-1 bg-primary hover:bg-primary-600"
            disabled={saving}
            data-testid="save-family-btn"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FamilyEditPage;
